import { Avatar } from '@repletoryreach/sdk-ui';

/** The WhatsApp mark, drawn rather than fetched: no network, and it themes. */
export function WhatsAppGlyph({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden focusable="false">
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2zm0 1.67c2.2 0 4.27.86 5.83 2.42a8.2 8.2 0 0 1 2.41 5.82c0 4.54-3.7 8.24-8.25 8.24a8.23 8.23 0 0 1-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.19 8.19 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.26-8.24zm-2.6 4.2c-.15 0-.4.06-.6.29-.21.23-.8.78-.8 1.9 0 1.12.82 2.2.93 2.36.11.15 1.6 2.44 3.87 3.42.54.23.96.37 1.29.48.54.17 1.04.15 1.43.09.44-.07 1.34-.55 1.53-1.08.19-.53.19-.98.13-1.08-.06-.09-.21-.15-.44-.26-.23-.12-1.34-.66-1.55-.74-.21-.08-.36-.11-.51.11-.15.23-.58.74-.71.89-.13.15-.26.17-.49.06-.23-.12-.96-.36-1.83-1.13-.68-.6-1.13-1.35-1.27-1.57-.13-.23-.01-.35.1-.47.1-.1.23-.26.34-.4.11-.13.15-.23.23-.38.08-.15.04-.29-.02-.4-.06-.12-.51-1.23-.7-1.68-.18-.44-.37-.38-.51-.39h-.43z" />
    </svg>
  );
}

/**
 * What identifies a contact when there is no name: the number, or (4 October
 * 2026) the address for an email-only contact. Never null, so nothing ever
 * renders the word "null".
 */
export function contactKeyOf(c: { msisdn: string | null; email?: string | null }): string {
  return c.msisdn ?? c.email ?? '';
}

/** The name, else the number, else the address. */
export function contactLabelOf(c: { name?: string | null; msisdn: string | null; email?: string | null }): string {
  return c.name ?? contactKeyOf(c);
}

/**
 * The contact cell: avatar, name, number and email in one block.
 *
 * The reference gives each row an identity rather than four flat columns of
 * text, and the practical gain is that the number and the email stop competing
 * with the name for the eye. The avatar colour is derived from the name by
 * sdk-ui, so it is stable per person.
 *
 * An email-only contact has no number line; its address stands where the
 * number would be, and is not repeated when it is already the title.
 */
export function ContactIdentity({
  name, msisdn, email
}: { name: string | null; msisdn: string | null; email: string | null }) {
  const title = contactLabelOf({ name, msisdn, email });
  return (
    <div className="rr-contacts__identity">
      <Avatar name={title} size={36} />
      <div className="rr-contacts__identity-text">
        <span className="rr-contacts__identity-name">{title}</span>
        {msisdn && (
          <span className="rr-contacts__identity-msisdn">
            <WhatsAppGlyph size={12} />
            {msisdn}
          </span>
        )}
        {email && email !== title && <span className="rr-contacts__identity-email">{email}</span>}
      </div>
    </div>
  );
}
